import { c as _c } from "react/compiler-runtime";
// Единый партнёрский кабинет (/partner, ТЗ 31.10.2026, решение: живёт в
// витрине). Вход - тот же, что у покупателя: телефон и код. Нет профиля
// партнёра - экран вступления (роль, имя, каналы). Есть - шапка с
// переключателем роли «UGC-креатор / Арбитражник» и три раздела:
// «Биржа товаров», «Мои артикулы», «Статистика и финансы».
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, Outlet, useOutletContext } from "react-router";
import { toast } from "sonner";
import { Skeleton } from "@osmix/ui/skeleton";
import { api, type UgcPartnerProfile, type UgcPartnerRole } from "@/api";
import { ErrorState } from "@/components/common/states";
import { partnerKey } from "@/components/partner/partner-ui";
import { SignInCard } from "@/components/profile/sign-in-card";
import { Segmented } from "@/components/ui/segmented";
import { cn } from "@/lib/utils";
import { useProfile, useWishlist } from "@/store";
import { PartnerJoin } from "./join";
export type PartnerContext = {
  partner: UgcPartnerProfile;
  userId: string;
};
export const usePartner = () => {
  return useOutletContext();
};
const TABS = [{
  to: "/partner/catalog",
  key: "catalog"
}, {
  to: "/partner/articles",
  key: "articles"
}, {
  to: "/partner/stats",
  key: "stats"
}] as const;
export function PartnerShell() {
  const $ = _c(26);
  const {
    t
  } = useTranslation();
  const token = useProfile(_temp);
  const user = useProfile(_temp2);
  const profile = useProfile(_temp3);
  const hydrateMe = useProfile(_temp4);
  const syncWishlist = useWishlist(_temp5);
  let t0;
  let t1;
  if ($[0] !== hydrateMe || $[1] !== token || $[2] !== user) {
    t0 = () => {
      if (token && !user) {
        hydrateMe().catch(_temp6);
      }
    };
    t1 = [hydrateMe, token, user];
    $[0] = hydrateMe;
    $[1] = token;
    $[2] = user;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[5] !== t) {
    t2 = () => {
      document.title = `${t("partner.title")} - OSMIX`;
    };
    t3 = [t];
    $[5] = t;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  const userId = user?.id;
  let t4;
  if ($[8] !== userId) {
    t4 = partnerKey(userId, "me");
    $[8] = userId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = Boolean(userId);
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = {
      queryKey: t4,
      queryFn: _temp7,
      enabled: t5
    };
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const me = useQuery(t6);
  if (!user || !profile) {
    let t7;
    if ($[13] !== t) {
      t7 = t("partner.signIn");
      $[13] = t;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] !== t7) {
      t8 = <p className="max-w-[440px] text-center text-[14px] leading-[18px] font-medium text-ds-secondary">{t7}</p>;
      $[15] = t7;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    let t9;
    if ($[17] !== syncWishlist) {
      t9 = <SignInCard onSignedIn={() => void syncWishlist()} />;
      $[17] = syncWishlist;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== t8 || $[20] !== t9) {
      t10 = <div className="mx-auto flex w-full max-w-[1140px] flex-col items-center gap-4 pt-2.5">{t8}{t9}</div>;
      $[19] = t8;
      $[20] = t9;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    return t10;
  }
  let t7;
  if ($[22] !== me || $[23] !== profile || $[24] !== user) {
    t7 = <div className="mx-auto flex w-full min-w-0 max-w-[1140px] flex-col gap-4 pt-2.5" data-testid="partner-cabinet">{me.isPending ? <Skeleton className="h-[320px] w-full rounded-[20px]" /> : me.error ? <ErrorState onRetry={() => void me.refetch()} /> : !me.data ? <PartnerJoin userId={user.id} defaultName={profile.name || [profile.firstName, profile.lastName].filter(Boolean).join(" ")} /> : <><PartnerHeader partner={me.data} userId={user.id} /><Outlet context={{
          partner: me.data,
          userId: user.id
        } satisfies PartnerContext} /></>}</div>;
    $[22] = me;
    $[23] = profile;
    $[24] = user;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
}
function _temp7() {
  return api.ugc.partner.me();
}
function _temp6() {}
function _temp5(s_3) {
  return s_3.syncFromServer;
}
function _temp4(s_2) {
  return s_2.hydrateMe;
}
function _temp3(s_1) {
  return s_1.profile;
}
function _temp2(s_0) {
  return s_0.user;
}
function _temp(s) {
  return s.token;
}
function PartnerHeader(t0) {
  const $ = _c(63);
  const {
    partner,
    userId
  } = t0;
  const {
    t
  } = useTranslation();
  const qc = useQueryClient();
  let t1;
  if ($[0] !== qc || $[1] !== t || $[2] !== userId) {
    t1 = next => {
      qc.setQueryData(partnerKey(userId, "me"), next);
      toast.success(t("partner.role.changed"));
    };
    $[0] = qc;
    $[1] = t;
    $[2] = userId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = e => toast.error(e instanceof Error ? e.message : t("common.error"));
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = {
      mutationFn: _temp8,
      onSuccess: t1,
      onError: t2
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const switchRole = useMutation(t3);
  let t4;
  if ($[9] !== t) {
    t4 = t("partner.title");
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <h1 className="text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-ds-ink md:text-[24px] md:leading-7">{t4}</h1>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = partner.displayName;
  const t7 = `partner.role.${partner.role}`;
  let t8;
  if ($[13] !== t || $[14] !== t7) {
    t8 = t(t7);
    $[13] = t;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== partner.displayName || $[17] !== t8) {
    t9 = <p className="text-[14px] leading-[18px] text-ds-secondary">{t6} · {t8}</p>;
    $[16] = partner.displayName;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t5 || $[20] !== t9) {
    t10 = <div className="flex min-w-0 flex-col gap-1">{t5}{t9}</div>;
    $[19] = t5;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t) {
    t11 = t("partner.role.switch");
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11) {
    t12 = <span className="text-[12px] leading-4 font-medium text-ds-label">{t11}</span>;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t) {
    t13 = t("partner.role.switch");
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = partner.role;
  let t15;
  if ($[28] !== partner.role || $[29] !== switchRole) {
    t15 = role_0 => {
      if (role_0 !== partner.role && !switchRole.isPending) {
        switchRole.mutate(role_0);
      }
    };
    $[28] = partner.role;
    $[29] = switchRole;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t) {
    t16 = t("partner.role.creator");
    $[31] = t;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t16) {
    t17 = {
      value: "creator",
      label: t16
    };
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t) {
    t18 = t("partner.role.arbitrage");
    $[35] = t;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t18) {
    t19 = {
      value: "arbitrage",
      label: t18
    };
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t17 || $[40] !== t19) {
    t20 = [t17, t19];
    $[39] = t17;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== partner.role || $[43] !== t13 || $[44] !== t15 || $[45] !== t20) {
    t21 = <Segmented aria-label={t13} value={t14} onValueChange={t15} options={t20} className="bg-white" />;
    $[42] = partner.role;
    $[43] = t13;
    $[44] = t15;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t12 || $[48] !== t21) {
    t22 = <div className="flex flex-col gap-1.5 md:items-end">{t12}{t21}</div>;
    $[47] = t12;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t10 || $[51] !== t22) {
    t23 = <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">{t10}{t22}</div>;
    $[50] = t10;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== t) {
    t24 = t("partner.title");
    $[53] = t;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t) {
    t25 = TABS.map(tab => <NavLink key={tab.key} to={tab.to} data-testid={`partner-tab-${tab.key}`} className={_temp9}>{t(`partner.tabs.${tab.key}`)}</NavLink>);
    $[55] = t;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t24 || $[58] !== t25) {
    t26 = <nav aria-label={t24} className="-mx-3 flex gap-2 overflow-x-auto px-3 [scrollbar-width:none] md:mx-0 md:px-0">{t25}</nav>;
    $[57] = t24;
    $[58] = t25;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t23 || $[61] !== t26) {
    t27 = <header className="flex flex-col gap-4 rounded-[20px] bg-ds-quaternary px-3 py-4 md:p-5">{t23}{t26}</header>;
    $[60] = t23;
    $[61] = t26;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  return t27;
}
function _temp9(t0) {
  const {
    isActive
  } = t0;
  return cn("inline-flex h-10 shrink-0 items-center rounded-full px-4 text-[14px] leading-[18px] font-semibold whitespace-nowrap outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ds-focus", isActive ? "bg-ds-primary text-white" : "bg-white text-ds-primary hover:bg-ds-gray");
}
function _temp8(role) {
  return api.ugc.partner.update({
    role
  });
}